import React, { useState, useRef, useEffect } from "react";

type Events = {
    id: number;
    starts: string;
    home_team: { name: string };
    away_team: { name: string };
    market: {
        line_name?: string;
        market_code: string;
        market_name: string;
        bets: {
            id: number;
            bet_code: string;
            bet_name: string;
            odds: number;
        }[];
    };
};

type SwipeDirection = "left" | "right" | "down" | "up";

type SwipedMarket = {
    event_id: number;
    bet_id: number;
    odds: number;
    market_name: string;
    market_code: string;
    bet_name: string;
    home_team: { name: string };
    away_team: { name: string };
};

export const useSwipeableMarketsQuickbetX = (
    allMarkets: Events[],
    skipEvent?: (event: Events) => void,
    showPayment?: (toggle: boolean) => void,
    onSwipeEventIndex?: (index: number) => void,
) => {
    const skipQueueRef = useRef<Events | null>(null);
    const [swipedMarkets, setSwipedMarkets] = useState<SwipedMarket[]>([]);
    const [feedback, setFeedback] = useState<{
        text: { side: string; odds: string; line_name?: string };
        direction: string;
    } | null>(null);
    const [previewValue, setPreviewValue] = useState<{
        text: { side: string; odds: string };
        direction: string;
    } | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const currentIndexRef = useRef(currentIndex);

    const filteredMarkets = allMarkets;

    const childRefs = useRef<React.RefObject<any>[]>([]);

    useEffect(() => {
        if (childRefs.current.length !== filteredMarkets.length) {
            childRefs.current = Array(filteredMarkets.length)
                .fill(0)
                .map(() => React.createRef<any>());
        }
    }, [filteredMarkets.length]);

    const updateCurrentIndex = (val: number) => {
        setCurrentIndex(val);
        currentIndexRef.current = val;
    };

    const canGoBack = currentIndex > 0;
    const canSwipe = currentIndex < filteredMarkets.length;

    const handlePreviewValue = (dir: SwipeDirection, market: Events) => {
        const betIndex = dir === "left" ? 0 : dir === "right" ? 2 : 1;
        setPreviewValue({
            text: {
                side: market.market.bets[betIndex].bet_name,
                odds: `${market.market.bets[betIndex].odds}`,
            },
            direction: dir,
        });

        const clearPreview = setTimeout(() => {
            setPreviewValue(null);
        }, 300);

        return () => clearTimeout(clearPreview);
    };

    const handleFeedback = (
        direction: SwipeDirection,
        market: Events,
        betIndex: number
    ) => {
        setPreviewValue(null);
        setFeedback({
            text: {
                side: market.market.bets[betIndex].bet_name,
                odds: `${market.market.bets[betIndex].odds}`,
                line_name: market.market.line_name || undefined,
            },
            direction,
        });
        setTimeout(() => setFeedback(null), 500);

        setSwipedMarkets(prev => [
            ...prev,
            {
                event_id: market.id,
                bet_id: market.market.bets[betIndex].id,
                odds: market.market.bets[betIndex].odds,
                home_team: market.home_team,
                away_team: market.away_team,
                market_name: market.market.market_name,
                market_code: market.market.market_code,
                bet_name: market.market.bets[betIndex].bet_name,
            },
        ]);
    };

    // const swiped = (direction: SwipeDirection, market: Events, index: number) => {
    //
    //     if (direction === "up") {
    //         skipQueueRef.current = market;
    //         updateCurrentIndex(index + 1);
    //         return;
    //     }
    //     const marketCode = market.market.market_code;
    //     const betIndex =
    //         ["MRFT", "DCFT", "HSH", "EHFT"].includes(marketCode) ?
    //             (direction === "left" ? 0 : direction === "right" ? 2 : 1) :
    //             (direction === "left" ? 0 : 1);
    //
    //     handleFeedback(direction, market, betIndex);
    //     updateCurrentIndex(index + 1);
    // };

    const swiped = (direction: SwipeDirection, market: Events, index: number) => {
        const newIndex = index + 1;

        if (direction === "up") {
            setFeedback({
                direction: 'up',
                text: { side: 'Skipped!', odds: '' }
            });
            setTimeout(() => setFeedback(null), 1500);

            // ✅ FIX: Викликаємо skipEvent миттєво і прямо тут
            if (skipEvent) {
                skipEvent(market);
            }
        } else {
            const marketCode = market.market.market_code;
            const betIndex = ["MRFT", "DCFT", "HSH", "EHFT"].includes(marketCode)
                ? (direction === "left" ? 0 : direction === "right" ? 2 : 1)
                : (direction === "left" ? 0 : 1);
            handleFeedback(direction, market, betIndex);
        }

        // Оновлюємо внутрішній індекс та індекс у батьківському компоненті
        updateCurrentIndex(newIndex);
        if (onSwipeEventIndex) {
            onSwipeEventIndex(newIndex)
        }
    };
    const outOfFrame = (marketId: number, index: number) => {

        const queuedEventToSkip = skipQueueRef.current;


        if (queuedEventToSkip && queuedEventToSkip.id === marketId) {
            if (skipEvent) {
                skipEvent(queuedEventToSkip);
            }
            skipQueueRef.current = null;
        }

        if (currentIndexRef.current <= index) {
            childRefs.current[index]?.current?.restoreCard();
        }
    };

    const swipe = async (dir: SwipeDirection) => {
        if (canSwipe && currentIndex < filteredMarkets.length) {
            await childRefs.current[currentIndex]?.current?.swipe(dir);
        }
    };



    // const goBack = async () => {
    //     if (!canGoBack) return;
    //     if (showPayment) {
    //         showPayment(false);
    //     }
    //     const newIndex = currentIndex - 1;
    //     updateCurrentIndex(newIndex);
    //     setSwipedMarkets(prev => prev.slice(0, -1));
    //     await childRefs.current[newIndex]?.current?.restoreCard();
    // };

    const goBack = async () => {
        if (!canGoBack) {
            return;
        }
        const indexToRestore = currentIndex - 1;
        setSwipedMarkets(prev => prev.slice(0, -1));

        updateCurrentIndex(indexToRestore);

        if (childRefs.current[indexToRestore] && childRefs.current[indexToRestore].current) {
            await childRefs.current[indexToRestore].current.restoreCard();
        }
        if (showPayment) {
            showPayment(false);
        }
    };
    return {
        marketsDEV: filteredMarkets,
        swipedMarkets,
        currentIndex,
        childRefs: childRefs.current,
        canGoBack,
        canSwipe,
        swiped,
        swipe,
        goBack,
        outOfFrame,
        skipQueueRef,
        feedback,
        handlePreviewValue,
        previewValue,
    };
};
